import MuiDialog from '@mui/material/Dialog'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { useCallback, useEffect, useId, useMemo, type ReactNode, type RefObject } from 'react'
import { useIntl } from 'react-intl'

import { cn, focusRing, insetFocusRing } from './cn'
import { X } from './icons'

export type DialogSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface DialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly title: string
  /** The archetype's max widths: 444, 600 (the default), 900, 1200 and 1536 px. */
  readonly size?: DialogSize
  /** Footer buttons, in order: outlined first, then the filled confirm. */
  readonly actions?: ReactNode
  readonly children?: ReactNode
  /** Goes on the panel; MUI's own styles win over a Tailwind class for what they set. */
  readonly className?: string
  /** The content's language, when it differs from the page's: the dialog renders outside it. */
  readonly lang?: string
  /** `alertdialog` for an urgent message that needs an answer; `dialog` by default. */
  readonly role?: 'dialog' | 'alertdialog'
  /** The id of the content that describes the dialog, read out when it opens. */
  readonly descriptionId?: string
  /** Whether a click outside the dialog closes it; true by default. Escape and the close button always do. */
  readonly isBackdropDismissible?: boolean
  /**
   * What takes focus once the dialog has opened, such as an alert dialog's safest answer, and again
   * whenever focus comes back to the dialog itself from the page behind. Otherwise MUI focuses the
   * dialog itself.
   */
  readonly initialFocusRef?: RefObject<HTMLElement | null>
}

// MUI's styles sit outside Tailwind's cascade layers and win over its classes, so the surface
// and backdrop are styled here rather than by class.
const paperSx: SxProps<Theme> = (theme) => ({
  bgcolor: 'background.default',
  borderColor: 'text.primary',
  boxShadow: (theme.vars ?? theme).palette.elevation.cardHover,
  display: 'flex',
  flexDirection: 'column',
  m: 4,
  maxHeight: '85vh',
  overflow: 'hidden',
  width: `calc(100% - ${theme.spacing(8)})`,
})

// Common black is the same in both schemes, so its literal value serves.
const backdropSx: SxProps<Theme> = (theme) => ({
  bgcolor: alpha(theme.palette.common.black, 0.64),
})

/**
 * A modal: a header with the title and a close button always named Close, as in the mockup, so it
 * never shares a name with a footer action such as Cancel, 20px from the top, a content slot that
 * stacks its children, and an optional 84px footer for the actions, which wrap when they do not fit
 * side by side, as at 320px. MUI holds focus inside it
 * while open, closes it on Escape and returns focus when it closes.
 */
export function Dialog({
  open,
  onClose,
  title,
  size = 'sm',
  actions,
  children,
  className,
  lang,
  role = 'dialog',
  descriptionId,
  isBackdropDismissible = true,
  initialFocusRef,
}: DialogProps) {
  const intl = useIntl()
  const titleId = useId()
  const close = useCallback(
    (_event: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (reason !== 'backdropClick' || isBackdropDismissible) {
        onClose()
      }
    },
    [isBackdropDismissible, onClose],
  )
  const slotProps = useMemo(
    () => ({
      paper: { sx: paperSx, ...(className === undefined ? {} : { className }) },
      backdrop: { sx: backdropSx },
      // After MUI's focus trap has focused the dialog as it opens.
      ...(initialFocusRef === undefined ? {} : { transition: { onEntered: () => initialFocusRef.current?.focus() } }),
    }),
    [className, initialFocusRef],
  )

  // The page behind may take focus while the dialog is open, as a page focusing its heading once it
  // loads; the focus trap then pulls focus back to the dialog itself, which sends it on to the chosen element.
  useEffect(() => {
    if (!open || initialFocusRef === undefined) {
      return undefined
    }
    const onFocusIn = (event: FocusEvent) => {
      const chosen = initialFocusRef.current
      const landed = event.target
      const from = event.relatedTarget instanceof Node ? event.relatedTarget : null
      if (chosen !== null && landed instanceof Element && landed !== chosen && landed.contains(chosen) && !landed.contains(from)) {
        chosen.focus()
      }
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [open, initialFocusRef])

  return (
    <MuiDialog
      open={open}
      onClose={close}
      maxWidth={size}
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      role={role}
      lang={lang}
      slotProps={slotProps}
    >
      <div className="flex shrink-0 items-start justify-between px-6 pt-5">
        <h2
          id={titleId}
          className="font-heading text-xl font-medium leading-[1.2] text-text-primary"
        >
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={intl.formatMessage({ id: 'ui.dialog.close' })}
          className={cn(
            'cursor-pointer rounded text-text-light transition hover:text-text-primary',
            focusRing,
          )}
        >
          <X size={24} />
        </button>
      </div>

      {/* Takes focus so the keyboard can scroll content that has nothing focusable in it. */}
      <div
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scroll region must take focus
        tabIndex={0}
        className={cn('flex flex-1 flex-col gap-6 overflow-y-auto px-6 py-4', insetFocusRing)}
      >
        {children}
      </div>

      {actions !== undefined && (
        <div className="flex min-h-[84px] shrink-0 flex-wrap items-center justify-end gap-2 px-6 py-4">
          {actions}
        </div>
      )}
    </MuiDialog>
  )
}
